import { createClient as createBrowserClient } from "@/lib/supabase/client";
import { safeGetUser } from "@/lib/supabase/safe-get-user";
import type {
  WishlistItem,
  WishlistResponse,
  WishlistMutationResponse,
  WishlistToggleResponse,
  WishlistStatusMap,
} from "@/types";

// Re-export types for convenience
export type { WishlistItem, WishlistResponse };

// getWishlist(브라우저에서 공급가 표를 직접 읽어 마크업 없이 KRW 로 표시하던 죽은 코드)는
// 10-16 삭제했다 — 공급가 표는 고객 키로 읽히지 않는다. 위시리스트 화면은 서버 컴포넌트
// (app/[locale]/profile/wishlist/page.tsx)가 공용 카드 경로로 그린다.

/**
 * Add a product to user's wishlist (client-side)
 */
export async function addToWishlist(productId: string): Promise<WishlistMutationResponse> {
  const supabase = createBrowserClient();

  const user = await safeGetUser(supabase, { retries: 1, baseDelayMs: 150 });

  if (!user) {
    return { success: false, error: "User not authenticated" };
  }

  const { error } = await supabase
    .from("wishlists")
    .insert({
      user_id: user.id,
      product_id: productId,
    });

  if (error) {
    // Handle duplicate entry error gracefully
    if (error.code === "23505") {
      return { success: true, error: null }; // Already in wishlist, treat as success
    }
    console.error("Failed to add to wishlist:", error);
    return { success: false, error: error.message };
  }

  return { success: true, error: null };
}

/**
 * Remove a product from user's wishlist (client-side)
 */
export async function removeFromWishlist(productId: string): Promise<WishlistMutationResponse> {
  const supabase = createBrowserClient();

  const user = await safeGetUser(supabase, { retries: 1, baseDelayMs: 150 });

  if (!user) {
    return { success: false, error: "User not authenticated" };
  }

  const { error } = await supabase
    .from("wishlists")
    .delete()
    .eq("user_id", user.id)
    .eq("product_id", productId);

  if (error) {
    console.error("Failed to remove from wishlist:", error);
    return { success: false, error: error.message };
  }

  return { success: true, error: null };
}

/**
 * Toggle product in wishlist (add if not exists, remove if exists) (client-side)
 */
export async function toggleWishlist(productId: string): Promise<WishlistToggleResponse> {
  const supabase = createBrowserClient();

  const user = await safeGetUser(supabase, { retries: 1, baseDelayMs: 150 });

  if (!user) {
    return { isInWishlist: false, error: "User not authenticated" };
  }

  // Insert-first atomic toggle. The old SELECT → insert/delete read-modify-
  // write let two rapid clicks interleave into an add+remove pair that left
  // the UI and DB permanently out of sync. The (user_id, product_id) unique
  // index makes the insert itself the existence check: success → it was
  // added; 23505 → it already existed, so the toggle means remove.
  const { error: insertError } = await supabase
    .from("wishlists")
    .insert({
      user_id: user.id,
      product_id: productId,
    });

  if (!insertError) {
    return { isInWishlist: true, error: null };
  }

  if (insertError.code === "23505") {
    const result = await removeFromWishlist(productId);
    return { isInWishlist: false, error: result.error };
  }

  console.error("Failed to toggle wishlist:", insertError);
  return { isInWishlist: false, error: insertError.message };
}

/**
 * Check if a product is in user's wishlist (client-side)
 */
export async function isInWishlist(productId: string): Promise<boolean> {
  const supabase = createBrowserClient();

  const user = await safeGetUser(supabase, { retries: 1, baseDelayMs: 150 });

  if (!user) {
    return false;
  }

  const { data } = await supabase
    .from("wishlists")
    .select("id")
    .eq("user_id", user.id)
    .eq("product_id", productId)
    .maybeSingle();

  return !!data;
}

/**
 * Get multiple wishlist statuses at once (client-side)
 */
export async function getWishlistStatuses(productIds: string[]): Promise<WishlistStatusMap> {
  const supabase = createBrowserClient();

  const user = await safeGetUser(supabase, { retries: 1, baseDelayMs: 150 });

  if (!user || productIds.length === 0) {
    return {};
  }

  const { data } = await supabase
    .from("wishlists")
    .select("product_id")
    .eq("user_id", user.id)
    .in("product_id", productIds);

  const statusMap: WishlistStatusMap = {};
  productIds.forEach(id => {
    statusMap[id] = false;
  });

  data?.forEach((item: { product_id: string }) => {
    statusMap[item.product_id] = true;
  });

  return statusMap;
}
